import { Capacitor } from "@capacitor/core";

/**
 * Let the native app paint edge to edge.
 *
 * Without `viewport-fit=cover` iOS lays the page out *inside* the safe area
 * and fills the status-bar and home-indicator strips with the webview's own
 * background (`ios.backgroundColor`, the canvas cream). That showed up as
 * cream bands above and below the leaf startup screen — the splash is
 * `position: fixed; inset: 0`, but "inset: 0" only reached as far as the
 * inset viewport.
 *
 * Turning it on makes the viewport the whole screen, so the splash covers
 * it, and — as a side effect — makes every `env(safe-area-inset-*)` in the
 * stylesheets resolve to a real number instead of 0. Those rules (the tab
 * bar, screen padding, the sticky footers) were written for this and were
 * silently no-ops until now; `native.css` adds the top inset they never
 * needed while the webview was inset for them.
 *
 * Native only, and applied before the first render: the marketing site keeps
 * the plain viewport it has always had, so nothing on the web moves.
 */
export function enableNativeFullBleed(): void {
  if (typeof document === "undefined") return;
  if (!Capacitor.isNativePlatform()) return;

  const meta = document.querySelector('meta[name="viewport"]');
  if (meta) {
    const content = meta.getAttribute("content") ?? "";
    if (!/viewport-fit\s*=/.test(content)) {
      meta.setAttribute(
        "content",
        content ? `${content}, viewport-fit=cover` : "viewport-fit=cover",
      );
    }
  }

  // Scopes native.css — nothing in it may apply to the website.
  document.documentElement.classList.add("zz-native");
}

export default enableNativeFullBleed;
